
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CompactTable } from '../../../components/ui/CompactTable';
import { Button } from '../../../components/ui/button';
import { Plus, Eye, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { DebitNoteModal } from '../components/DebitNoteModal';
import { getSupplierBillStatusMeta } from '../utils/statuses';
import { legacyApi, SupplierBill } from '../../../services/legacyApi';
import { TableFilterBar } from '../../../components/table-filters/TableFilterBar';
import { useTableFilterState } from '../../../hooks/useTableFilterState';
import { FlowEntryBanner } from '../../../components/workflow/FlowEntryBanner';

export const LegacySupplierBills: React.FC = () => {
    const PAGE_SIZE = 50;
    const navigate = useNavigate();
    const [bills, setBills] = useState<SupplierBill[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const [debouncedQ, setDebouncedQ] = useState('');
    const { state, setFilter, clearAll, isDirty } = useTableFilterState({ q: '', status: 'all', ageing: 'all' });

    // Modal State
    const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
    const [selectedBillForReturn, setSelectedBillForReturn] = useState<SupplierBill | null>(null);

    useEffect(() => {
        const t = window.setTimeout(() => setDebouncedQ(state.q.trim()), 250);
        return () => window.clearTimeout(t);
    }, [state.q]);

    const loadBills = async (reset: boolean = true) => {
        const nextOffset = reset ? 0 : bills.length;
        try {
            if (reset) setLoading(true);
            else setLoadingMore(true);
            const response = await legacyApi.purchasing.listBillsPaged({
                q: debouncedQ || undefined,
                status_id: state.status !== 'all' ? Number(state.status) : undefined,
                ageing: state.ageing !== 'all' ? state.ageing : undefined,
                limit: PAGE_SIZE,
                offset: nextOffset,
            });
            setBills((prev) => (reset ? response.items : [...prev, ...response.items]));
            setTotalCount(response.total);
            setHasMore(response.has_more);
        } catch (error) {
            console.error('Failed to load bills:', error);
            toast.error('Failed to load supplier bills');
        } finally {
            if (reset) {
                setLoading(false);
                setHasLoadedOnce(true);
            }
            else setLoadingMore(false);
        }
    };

    useEffect(() => {
        loadBills(true);
    }, [debouncedQ, state.status, state.ageing]);

    const handleReturnClick = (bill: SupplierBill) => {
        setSelectedBillForReturn(bill);
        setIsReturnModalOpen(true);
    };

    if (!hasLoadedOnce && loading) return <div className="p-4">Loading Supplier Bills...</div>;

    const formatDate = (dateStr: string) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('en-GB');
    };

    const formatCurrency = (amount: number) => {
        return amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const getStatusBadge = (statusId: number) => {
        const meta = getSupplierBillStatusMeta(statusId);
        return (
            <span className={`px-2 py-1 rounded text-xs ${meta.badgeClass} font-medium inline-block w-24 text-center`}>
                {meta.label}
            </span>
        );
    };

    const getBillAgeingDays = (bill: SupplierBill): number | null => {
        const statusId = Number(bill.status_id || 1);
        if (statusId === 3 || statusId === 4) return null; // paid/cancelled
        if (!bill.bill_date) return null;
        const parsed = new Date(bill.bill_date);
        if (Number.isNaN(parsed.getTime())) return null;
        const billDay = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const diffDays = Math.floor((today.getTime() - billDay.getTime()) / (1000 * 60 * 60 * 24));
        return Math.max(0, diffDays);
    };

    const getAgeingBucket = (ageingDays: number | null): string => {
        if (ageingDays === null) return 'none';
        if (ageingDays <= 30) return '0-30';
        if (ageingDays <= 60) return '31-60';
        if (ageingDays <= 90) return '61-90';
        return '90+';
    };

    const columns = [
        {
            header: "Bill #",
            accessor: (b: SupplierBill) => `HC/BILL/${String(b.bill_id).padStart(4, '0')}`,
            className: 'font-mono whitespace-nowrap w-32'
        },
        {
            header: "Date",
            accessor: (b: SupplierBill) => formatDate(b.bill_date),
            className: 'w-24 whitespace-nowrap'
        },
        {
            header: "Supplier",
            accessor: (b: SupplierBill) => b.supplier_name || '-',
            className: 'font-medium min-w-[200px] max-w-[300px] truncate'
        },
        {
            header: "GRN #",
            accessor: (b: SupplierBill) => (
                <Button
                    variant="link"
                    className="p-0 h-auto text-blue-600"
                    onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/legacy/grn/${b.grn_id}`);
                    }}
                >
                    HC/GRN/{String(b.grn_id).padStart(4, '0')}
                </Button>
            ),
            className: 'font-mono whitespace-nowrap w-32'
        },
        {
            header: "Reference",
            accessor: (b: SupplierBill) => b.reference_no || '-',
            className: 'min-w-[150px] max-w-[200px] truncate'
        },
        {
            header: "Subtotal",
            accessor: (b: SupplierBill) => formatCurrency(b.sub_total),
            className: 'w-28 text-right font-medium tabular-nums'
        },
        {
            header: "VAT",
            accessor: (b: SupplierBill) => formatCurrency(b.vat_amount),
            className: 'w-24 text-right tabular-nums'
        },
        {
            header: "Total",
            accessor: (b: SupplierBill) => formatCurrency(b.grand_total),
            className: 'w-28 text-right font-semibold tabular-nums'
        },
        {
            header: "Status",
            accessor: (b: SupplierBill) => getStatusBadge(b.status_id),
            className: 'w-24 text-center'
        },
        {
            header: "Ageing",
            accessor: (b: SupplierBill) => {
                const ageingDays = getBillAgeingDays(b);
                return ageingDays === null ? '-' : ageingDays;
            },
            className: 'w-24 text-right font-mono tabular-nums'
        },
        {
            header: "Actions",
            accessor: (b: SupplierBill) => (
                <div className="flex gap-1 justify-end">
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/purchasing/bills/${b.bill_id}`);
                        }}
                        title="View Details"
                    >
                        <Eye className="w-4 h-4 text-green-600" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleReturnClick(b);
                        }}
                        title="Create Debit Note (Return)"
                    >
                        <RotateCcw className="w-4 h-4 text-orange-600" />
                    </Button>
                </div>
            ),
            className: "text-right"
        }
    ];

    return (
        <div className="h-full flex flex-col space-y-6">
            <div className="flex items-center justify-between space-y-2">
                <div>
                    <h2 className="text-3xl font-bold tracking-tight text-foreground">Supplier Bills</h2>
                    <p className="text-muted-foreground">Manage supplier bills and accounts payable</p>
                </div>
                <Button onClick={() => navigate('/purchasing/bills/new')}>
                    <Plus className="h-4 w-4 mr-2" /> New Bill
                </Button>
            </div>
            <FlowEntryBanner
                title="Supplier Bill Flow"
                pathLabel="PO to Supplier Bill"
                nextHint="Post bills after GRN reconciliation, then mark paid to complete AP lifecycle."
                helpModule="purchasing"
            />
            <TableFilterBar
                searchValue={state.q}
                onSearchChange={(value) => setFilter('q', value)}
                searchPlaceholder="Search bill, supplier, GRN..."
                controls={[
                    {
                        key: 'status',
                        label: 'Status',
                        type: 'select',
                        value: state.status,
                        onChange: (value) => setFilter('status', value),
                        options: [
                            { value: 'all', label: 'All Statuses' },
                            { value: '1', label: 'Pending' },
                            { value: '2', label: 'Posted' },
                            { value: '5', label: 'Partially Paid' },
                            { value: '3', label: 'Paid' },
                            { value: '4', label: 'Cancelled' }
                        ]
                    },
                    {
                        key: 'ageing',
                        label: 'Ageing',
                        type: 'select',
                        value: state.ageing,
                        onChange: (value) => setFilter('ageing', value),
                        options: [
                            { value: 'all', label: 'All Ageing' },
                            { value: '0-30', label: '0-30 Days' },
                            { value: '31-60', label: '31-60 Days' },
                            { value: '61-90', label: '61-90 Days' },
                            { value: '90+', label: '90+ Days' },
                            { value: 'none', label: 'Not Applicable' }
                        ]
                    }
                ]}
                isDirty={isDirty}
                onClear={clearAll}
                rightSlot={`${bills.length} of ${totalCount}`}
            />
            <div className="flex-1 overflow-auto rounded-md border">
                <>
                    <CompactTable
                        data={bills}
                        columns={columns}
                        keyField="bill_id"
                        loading={loading}
                        onRowClick={(row) => navigate(`/purchasing/bills/${row.bill_id}`)}
                    />
                    {hasMore && (
                        <div className="flex justify-center mt-4">
                            <Button variant="ghost" onClick={() => loadBills(false)} disabled={loadingMore}>
                                {loadingMore ? 'Loading...' : 'Load older supplier bills'}
                            </Button>
                        </div>
                    )}
                </>
            </div>
            {selectedBillForReturn && (
                <DebitNoteModal
                    bill={selectedBillForReturn}
                    isOpen={isReturnModalOpen}
                    onClose={() => {
                        setIsReturnModalOpen(false);
                        setSelectedBillForReturn(null);
                    }}
                    onSuccess={() => {
                        toast.success("Debit Note created");
                        loadBills();
                    }}
                />
            )}
        </div>
    );
};
